import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Rocket, CheckCircle2, Clock, ArrowRight, ChevronDown, Check } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { usePanelT } from "../../hooks/usePanelT";
import { PLATFORM_CATALOG } from "../../data/platformCatalog";

function usd(n) {
    return `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function PlatformDetail({ item, lang, minTopup, fees, t }) {
    const c = item[lang] || item.en;
    const white = Number(fees?.whiteHat ?? 4);
    const net = minTopup * (1 - white / 100);
    return (
        <div data-testid={`first-deposit-detail-${item.key}`} className="rounded-xl border border-white/10 bg-background/60 p-4 grid md:grid-cols-[1fr_220px] gap-4">
            <div className="min-w-0">
                <p className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: item.color === "#FFFC00" ? "#e6e300" : item.color }}>{c.kind}</p>
                <h4 className="font-display font-bold text-lg mt-0.5">{c.title}</h4>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.desc}</p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    {c.features.map((f) => (
                        <li key={f} className="flex items-start gap-1.5 text-xs text-foreground/85"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />{f}</li>
                    ))}
                </ul>
            </div>
            <div className="space-y-2">
                <div className="rounded-lg border border-primary/30 bg-primary/[0.06] px-3 py-2.5">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("fd.minLabel")}</p>
                    <p className="font-display font-black text-2xl tabular-nums" data-testid="first-deposit-min-topup">{usd(minTopup)}</p>
                </div>
                <div className="rounded-lg border border-white/10 px-3 py-2.5">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">{t("fd.feeTitle")}</p>
                    <div className="space-y-1">
                        {["whiteHat", "grayHat", "casino"].map((v) => (
                            <div key={v} className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">{v === "casino" ? t("v.casinoShort") : t(`v.${v}`)}</span>
                                <span className="font-semibold tabular-nums" data-testid={`first-deposit-fee-${v}`}>{Number(fees?.[v] ?? (v === "casino" ? 10 : v === "grayHat" ? 7 : 4))}%</span>
                            </div>
                        ))}
                    </div>
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug" data-testid="first-deposit-fee-note">{t("fd.feeNote", { min: minTopup, fee: white, f: (minTopup - net).toFixed(2), net: net.toFixed(2) })}</p>
            </div>
        </div>
    );
}

export function FirstDepositGuide({ wallet, accountsCount }) {
    const { t, lang } = usePanelT();
    const [policy, setPolicy] = useState(null);
    const [deposits, setDeposits] = useState([]);
    const [platform, setPlatform] = useState("meta_standard");
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem("adrise_first_deposit_guide_collapsed") === "1");

    useEffect(() => {
        api.get("/me/ad-policy").then(({ data }) => setPolicy(data)).catch(() => {});
        api.get("/me/deposits").then(({ data }) => setDeposits(data)).catch(() => {});
    }, []);

    const item = PLATFORM_CATALOG.find((p) => p.key === platform) || PLATFORM_CATALOG[0];
    const minTopup = Number(policy?.min_topups?.[platform] ?? 250);
    const required = minTopup;
    const balance = Number(wallet?.usd_balance ?? 0);
    const progress = Math.max(0, Math.min(1, required > 0 ? balance / required : 0));
    const ready = balance >= required;
    const pendingDeposits = useMemo(() => deposits.filter((d) => d.status !== "credited"), [deposits]);
    const pendingUsd = pendingDeposits.reduce((s, d) => s + Number(d.amount_usd || 0), 0);

    if (accountsCount > 0) return null;

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        localStorage.setItem("adrise_first_deposit_guide_collapsed", next ? "1" : "0");
    };

    if (collapsed) {
        return (
            <button type="button" onClick={toggle} data-testid="first-deposit-guide-show" className="w-full rounded-2xl border border-primary/25 bg-primary/[0.05] px-4 py-3 text-sm flex items-center justify-between hover:bg-primary/[0.08] transition-colors">
                <span className="inline-flex items-center gap-2 font-medium"><Rocket className="w-4 h-4 text-primary" /> {ready ? t("fd.titleReady") : t("fd.title")}</span>
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">{t("fd.show")} <ChevronDown className="w-3.5 h-3.5" /></span>
            </button>
        );
    }

    const steps = [
        { n: 1, label: t("fd.step1"), state: ready ? "done" : "active" },
        { n: 2, label: t("fd.step2"), state: ready ? "active" : "todo" },
        { n: 3, label: t("fd.step3"), state: "todo" },
    ];

    return (
        <div data-testid="first-deposit-guide" className="relative overflow-hidden rounded-2xl border border-primary/30 bg-card p-4 md:p-6">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.09] via-transparent to-transparent pointer-events-none" />
            <div className="relative space-y-5">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">{t("fd.eyebrow")}</p>
                        <h3 className="font-display font-bold text-lg md:text-2xl mt-1" data-testid="first-deposit-guide-title">{ready ? t("fd.titleReady") : t("fd.title")}</h3>
                        <p className="text-sm text-muted-foreground mt-1 hidden sm:block">{t("fd.sub")}</p>
                    </div>
                    <button type="button" onClick={toggle} data-testid="first-deposit-guide-hide" className="text-xs text-muted-foreground hover:text-foreground shrink-0 px-2 py-1">{t("fd.hide")}</button>
                </div>

                <ol className="grid sm:grid-cols-3 gap-2 md:gap-3">
                    {steps.map((s) => (
                        <li key={s.n} data-testid={`first-deposit-step-${s.n}`} data-state={s.state} className={`rounded-xl border p-3 flex items-center gap-3 ${s.state === "active" ? "border-primary/50 bg-primary/[0.08]" : s.state === "done" ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-white/10 bg-background/40"}`}>
                            <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 text-xs font-bold ${s.state === "done" ? "bg-emerald-500 text-white" : s.state === "active" ? "bg-primary text-primary-foreground" : "bg-white/[0.06] text-muted-foreground"}`}>
                                {s.state === "done" ? <CheckCircle2 className="w-4 h-4" /> : s.n}
                            </span>
                            <div className="min-w-0">
                                <p className={`text-sm font-medium leading-tight ${s.state === "todo" ? "text-muted-foreground" : ""}`}>{s.label}</p>
                                {s.state === "done" && <p className="text-[10px] font-mono uppercase text-emerald-400 mt-0.5">{t("fd.done")}</p>}
                                {s.state === "active" && s.n === 2 && <p className="text-[10px] font-mono uppercase text-primary mt-0.5">{t("fd.ready")}</p>}
                            </div>
                        </li>
                    ))}
                </ol>

                {/* Platform picker — one card per account type */}
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">{t("fd.pick")}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {PLATFORM_CATALOG.map((p) => {
                            const active = platform === p.key;
                            const min = Number(policy?.min_topups?.[p.key] ?? 250);
                            return (
                                <button key={p.key} type="button" onClick={() => setPlatform(p.key)} data-testid={`first-deposit-platform-${p.key}`} data-active={active} className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${active ? "border-primary/50 bg-primary/[0.1]" : "border-white/10 bg-background/40 hover:border-white/25"}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={`text-sm font-semibold ${active ? "text-primary" : ""}`}>{p.label}</span>
                                        <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{p.short}</span>
                                    </div>
                                    <p className="text-[11px] font-mono text-muted-foreground mt-0.5">{t("fd.minLabel")}: <span className="text-foreground/90">{usd(min)}</span></p>
                                </button>
                            );
                        })}
                    </div>
                    <PlatformDetail item={item} lang={lang} minTopup={minTopup} fees={policy?.fee_by_vertical} t={t} />
                </div>

                {/* Progress */}
                <div className="rounded-xl border border-white/10 bg-background/50 p-4 space-y-3">
                    <div className="flex items-end justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("fd.have")}</p>
                            <p className="font-display font-black text-2xl tabular-nums" data-testid="first-deposit-balance">{usd(balance)}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t("fd.need")} · {item.label}</p>
                            <p className="font-display font-black text-2xl tabular-nums" data-testid="first-deposit-required">{usd(required)}</p>
                        </div>
                    </div>
                    <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                        <div data-testid="first-deposit-progress" className={`h-full rounded-full transition-all duration-700 ${ready ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${Math.round(progress * 100)}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-mono text-muted-foreground">{Math.round(progress * 100)}%</span>
                        {!ready && <span className="font-mono text-foreground/85" data-testid="first-deposit-remaining">{t("fd.remaining")}: {usd(required - balance)}</span>}
                    </div>
                    {pendingDeposits.length > 0 && (
                        <p className="text-xs text-yellow-300 inline-flex items-center gap-1.5" data-testid="first-deposit-pending"><Clock className="w-3.5 h-3.5" /> {t("fd.pending", { n: pendingDeposits.length, usd: pendingUsd.toFixed(0) })}</p>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                    {ready ? (
                        <Link to="/panel/ad-accounts" data-testid="first-deposit-cta-request" className="flex-1">
                            <Button className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"><Rocket className="w-4 h-4 mr-2" /> {t("fd.ctaRequest")} <ArrowRight className="w-4 h-4 ml-2" /></Button>
                        </Link>
                    ) : (
                        <>
                            <Link to="/panel/wallet" data-testid="first-deposit-cta-fund" className="flex-1">
                                <Button className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"><Wallet className="w-4 h-4 mr-2" /> {t("fd.ctaFund")} <ArrowRight className="w-4 h-4 ml-2" /></Button>
                            </Link>
                            <Link to="/panel/ad-accounts" data-testid="first-deposit-cta-request-secondary" className="sm:w-auto">
                                <Button variant="outline" className="w-full h-11 border-white/15 bg-white/[0.02] hover:bg-white/[0.06]">{t("fd.ctaRequest")}</Button>
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
